/**
 * Plain-language view of audit records: a category, an icon and a sentence for each action code. The raw
 * code stays visible next to the sentence (records are evidence; the sentence is only a reading aid).
 */
export type AuditCategory = 'access' | 'partners' | 'keys' | 'approvals' | 'money' | 'rounds' | 'cameras' | 'promotions' | 'data' | 'other';

export const CATEGORY: Record<AuditCategory, { label: string; icon: string }> = {
  access: { label: 'Accounts & sign-in', icon: '👤' },
  partners: { label: 'Operators & sites', icon: '🏢' },
  keys: { label: 'Keys & integration', icon: '🔑' },
  approvals: { label: 'Approvals', icon: '✅' },
  money: { label: 'Refunds & money', icon: '💶' },
  rounds: { label: 'Rounds & games', icon: '🎮' },
  cameras: { label: 'Cameras', icon: '📷' },
  promotions: { label: 'Promotions & rewards', icon: '🎁' },
  data: { label: 'Exports & labels', icon: '📄' },
  other: { label: 'Other', icon: '•' },
};

const KIND: Record<string, { what: string; cat: AuditCategory }> = {
  REVIEW_DECISION: { what: 'a round review decision', cat: 'rounds' },
  ROUND_VOID: { what: 'voiding a round (refund of every bet)', cat: 'money' },
  GOODWILL_REFUND: { what: 'a goodwill refund', cat: 'money' },
  MANUAL_ADJUSTMENT: { what: 'a manual balance adjustment', cat: 'money' },
  CONFIG_CHANGE: { what: 'a configuration change', cat: 'rounds' },
  KEY_ROTATION: { what: 'a production API key', cat: 'keys' },
  PARTNER_CONNECTION: { what: 'a partner wallet / host connection', cat: 'keys' },
  REAL_MONEY: { what: 'switching sites to REAL money', cat: 'money' },
};

/** Exact phrases: [category, verb phrase] — the sentence is "<actor> <phrase> <target>". */
const EXACT: Record<string, [AuditCategory, string]> = {
  'account.created': ['access', 'created the account'],
  'account.deleted': ['access', 'disabled the account'],
  'account.reset': ['access', 'reset the password of'],
  'account.role_changed': ['access', 'changed the role of'],
  'account.password_changed': ['access', 'changed their own password'],
  'account.mfa_enrolled': ['access', 'set up their authenticator app'],
  OPERATOR_CREATED: ['partners', 'created the operator'],
  OPERATOR_UPDATED: ['partners', 'updated the operator'],
  OPERATOR_SUSPENDED: ['partners', 'suspended the operator'],
  OPERATOR_ACTIVATED: ['partners', 'reactivated the operator'],
  OPERATOR_KIND_SET: ['partners', 'changed the operator type (regular / aggregator) of'],
  BRAND_CREATED: ['partners', 'created the site'],
  BRAND_UPDATED: ['partners', 'updated the site'],
  BRAND_SUSPENDED: ['partners', 'suspended the site'],
  BRAND_ACTIVATED: ['partners', 'reactivated the site'],
  SITE_AUTO_CREATED: ['partners', 'created a site automatically on its first launch:'],
  SITE_SUSPENDED: ['partners', 'suspended the whole site'],
  SITE_ACTIVATED: ['partners', 'reactivated the whole site'],
  SITE_UPDATED: ['partners', 'updated the whole site'],
  REAL_MONEY_OFF: ['money', 'switched back to demo (real money off) on'],
  CURRENCY_LIMITS_ADDED: ['rounds', 'added starting risk limits for the currency'],
  ENTITLEMENT_SET: ['partners', 'enabled a game for'],
  ENTITLEMENT_DISABLED: ['partners', 'disabled a game for'],
  WALLET_CONFIG_SET: ['keys', 'set the wallet connection of'],
  WALLET_SECRETS_ROTATED: ['keys', 'generated new wallet signing secrets for'],
  CALLBACK_HOST_REGISTERED: ['keys', 'allowed the callback host'],
  CALLBACK_HOST_REMOVED: ['keys', 'removed the callback host'],
  SANDBOX_KEY_ISSUED: ['keys', 'issued the sandbox API key'],
  SANDBOX_KEY_ROTATED: ['keys', 'replaced the sandbox API key'],
  KEY_REVOKED: ['keys', 'revoked the API key'],
  PARTNER_CERTIFICATION_RECORDED: ['keys', 'recorded the partner certification of'],
  WEBHOOK_REPLAYED: ['keys', 're-sent the webhook delivery'],
  PAUSE_NEW_ROUNDS: ['rounds', 'paused new rounds of'],
  RESUME_NEW_ROUNDS: ['rounds', 'resumed new rounds of'],
  PRICING_MODEL_SUSPEND: ['rounds', 'suspended the pricing model'],
  CAMERA_ACTIVATE: ['cameras', 'activated the camera'],
  CAMERA_DEACTIVATE: ['cameras', 'took out of service the camera'],
  CAMERA_STANDBY: ['cameras', 'put on standby the camera'],
  CAMERA_HEALTH_CHECK_FORCED: ['cameras', 'forced a health check of'],
  'camera.line_drawn': ['cameras', 'drew a new counting line on'],
  'camera.onboard_started': ['cameras', 'started adding the camera'],
  COUNT_LABELS_SAVED: ['data', 'saved hand-counted labels for'],
  EXPORT_FINANCE_CSV: ['data', 'exported a finance CSV'],
  CAMPAIGN_CREATED: ['promotions', 'created the campaign'],
  LEADERBOARD_CREATED: ['promotions', 'created the leaderboard'],
  LEADERBOARD_EXCLUSION: ['promotions', 'excluded a player from'],
  DROP_CREATED: ['promotions', 'created the drop chest'],
  DROP_CANCELLED: ['promotions', 'cancelled the drop chest'],
  ENGAGEMENT_SWITCH: ['promotions', 'switched rewards on/off for'],
};

export interface AuditView {
  cat: AuditCategory;
  phrase: string;
  automatic: boolean;
}

export const isAutomatic = (actor: string) => actor === 'cli-operator' || actor === 'system' || actor.includes(':');

export function actorName(actor: string, displayName?: string | null): string {
  if (actor === 'cli-operator') return 'Automation (camera pipeline / CLI)';
  if (actor === 'system') return 'System';
  if (actor.startsWith('partner-api:')) return `Partner API (${actor.slice(12)})`;
  return displayName ? `${displayName} (${actor})` : actor;
}

const humanize = (code: string) => code.replace(/[._]/g, ' ').toLowerCase();

export function describe(action: string, actor: string): AuditView {
  const automatic = isAutomatic(actor);
  const hit = EXACT[action];
  if (hit) return { cat: hit[0], phrase: hit[1], automatic };
  const m = /^(REQUEST|APPROVE|REJECT|APPLY_FAILED)_(.+)$/.exec(action);
  if (m) {
    const k = KIND[m[2]!] ?? { what: humanize(m[2]!), cat: 'approvals' as AuditCategory };
    const verb = { REQUEST: 'asked for approval of', APPROVE: 'approved', REJECT: 'rejected', APPLY_FAILED: 'could not apply the approved' }[m[1] as 'REQUEST'];
    return { cat: k.cat, phrase: `${verb} ${k.what} —`, automatic };
  }
  if (/^CAMERA|^camera\./.test(action)) return { cat: 'cameras', phrase: `${humanize(action.replace(/^camera\.|^CAMERA_/, ''))} —`, automatic };
  if (/^account\./.test(action)) return { cat: 'access', phrase: `${humanize(action.slice(8))} —`, automatic };
  return { cat: 'other', phrase: `${humanize(action)} —`, automatic };
}

/** Approval records belong to the approvals filter too (their category is the subject: money, keys…). */
export const isApproval = (action: string) => /^(REQUEST|APPROVE|REJECT|APPLY_FAILED)_/.test(action);
